import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Spinner, Alert, Form, Col, Row, Button, Modal } from "react-bootstrap";
import { selectUserDetails, selectMappedRoles } from "../../redux/slices/userSlice";
import { setJobId } from "../../redux/slices/jobIdSlice";
import { setZones } from "../../redux/slices/zoneCountSlice";
import { checkoutJob } from "../../redux/slices/checkoutSlice";
import { useNavigate } from "react-router-dom";
// Import centralized date utilities
import { formatDateLocalEnhanced } from '../../utils/dateUtils';
import { calculateEngineeringDueDate } from '../../utils/engineeringCalculations';
import { selectPtoEntries } from '../../redux/slices/ptoSlice';
import { selectHolidays } from '../../redux/slices/holidaysSlice';
// Import longLead slice actions
import { fetchLongLead } from "../../redux/slices/longLeadSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { selectChangeOrderDetails } from "../../redux/selectors";
import axiosInstance from "../../axios/axiosConfig";
import EngineeringPortalNoticeBanner from '../common/EngineeringPortalNoticeBanner';
// Import helpers for notice detection
import { hasTbd as checkHasTbd } from "../JobSchedulePage/utils/highlightingUtils";
import { hasIncompleteChangeOrderTasks } from "../JobSchedulePage/utils/jobHelpers";
import { usePermissions } from "../../hooks/usePermissions";

function AssignedEngineeringJobs({ 
  jobs = [], 
  loading = false, 
  error = null, 
  onJobCompletion,
  userDropdowns = {}
}) {
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const mappedRoles = useSelector(selectMappedRoles);
  const { isAdmin } = usePermissions();
  const isAdminUser = isAdmin();
  const roles = useMemo(() => ({
    ...(mappedRoles || {}),
    Admin: isAdminUser,
  }), [mappedRoles, isAdminUser]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Always call useSelector hooks - React hooks must be called at the top level
  const longLeadCriteria = useSelector(state => state.longLead.data);
  const { options } = useSelector((state) => state.dropdown);
  const apiUrls = useSelector(selectApiUrls);
  const ptoEntries = useSelector(selectPtoEntries);
  const holidays = useSelector(selectHolidays);
  const tbdList = useSelector((state) => state.tbdApi.tbdList);
  const changeOrderDetails = useSelector(selectChangeOrderDetails);

  const getJobId = useCallback((job) => {
    if (!job) return null;
    return job.ID || job.ProjectID || job.JobID || job.projectId || null;
  }, []);

  const getFeedbackProjectId = useCallback((job) => {
    if (!job) return null;
    return job.ProjectId ?? job.ProjectID ?? job.ID ?? job.JobID ?? job.projectId ?? null;
  }, []);

  const buildEngineeringFeedbackPayload = useCallback((job, userRole, feedbackOverrides = {}) => {
    const projectId = getFeedbackProjectId(job);

    return {
      ProjectId: projectId,
      ProjectID: projectId,
      UserRole: userRole,
      ...feedbackOverrides
    };
  }, [getFeedbackProjectId]);
  
  // Callbacks to check for TBDs and incomplete change order tasks
  const hasTbd = useCallback((jobId) => {
    return checkHasTbd(jobId, tbdList);
  }, [tbdList]);
  
  const hasIncompleteTasks = useCallback((jobId) => {
    return hasIncompleteChangeOrderTasks(jobId, changeOrderDetails);
  }, [changeOrderDetails]);
  
  // Helper function to get criteria flag for a specific job
  const getLongLeadCriteriaFlag = useCallback((job) => {
    if (!job) return false;

    const projectId = getJobId(job);
    
    if (!projectId || !longLeadCriteria[projectId]) return false;
    return longLeadCriteria[projectId].criteriaMetFlag;
  }, [longLeadCriteria, getJobId]);

  // Local state for UI functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'RelevantDueDate', direction: 'ascending' });
  const [productionSortConfig, setProductionSortConfig] = useState({ key: 'CurrentPlanShipDate', direction: 'ascending' });
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [hideShipped, setHideShipped] = useState(true); // Default to hiding shipped jobs

  // Local state for job updates to reflect changes immediately
  const [localJobUpdates, setLocalJobUpdates] = useState({});
  // Add state for the completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [completionField, setCompletionField] = useState("");
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [completionError, setCompletionError] = useState(null);
  const [completionSuccess, setCompletionSuccess] = useState(false);
  const [selectedCompletionType, setSelectedCompletionType] = useState(null);
  const [longLeadsCompletionType, setLongLeadsCompletionType] = useState("Yes"); // New state for long leads type

  // Add state for notes modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedJobForNotes, setSelectedJobForNotes] = useState(null);
  const [notesField, setNotesField] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [originalNotesValue, setOriginalNotesValue] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [notesSuccess, setNotesSuccess] = useState(false);
  const previousJobs = useRef(jobs);
  const hasActiveEditor = showCompletionModal || showNotesModal || savingCompletion || savingNotes;
  useRealtimeDraft(hasActiveEditor);
  useEffect(() => {
    if (hasActiveEditor || previousJobs.current === jobs) return;
    previousJobs.current = jobs;
    setLocalJobUpdates({});
  }, [jobs, hasActiveEditor]);

  // Add state for engineering feedback form
  const [engineeringFeedback, setEngineeringFeedback] = useState({
    CSOUtilizationPercent: null,
    CSOUtilizationReason: "",
    CSOComments: "",
    ReasonLate: "",
    ReasonLateComments: ""
  });
  const [feedbackValidationError, setFeedbackValidationError] = useState(null);
  
  // Helper function to check if completion is late
  const isCompletionLate = useCallback((job, completionField) => {
    if (!job || !completionField) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let dueDate = null;
    if (completionField === 'MechanicalDone') {
      dueDate = job.MechanicalDue;
    } else if (completionField === 'ElectricalDone') {
      dueDate = job.ElectricalDue;
    }
    
    if (!dueDate) return false;
    
    const [year, month, day] = dueDate.split('-').map(num => parseInt(num, 10));
    const due = new Date(year, month - 1, day);
    due.setHours(0, 0, 0, 0);
    
    return today > due; // Today is after the due date
  }, []);

  /****************Add dropdown options here*************************/
  const reasonLateOptions =
    options?.filter((option) => option.DropdownType === "Reason Late")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const csoUtilizationReasonOptions =
    options?.filter((option) => option.DropdownType === "CSO Utilization Reason")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  /***************************************************************** */

  // Helper function to get updated job data for modal display
  const getUpdatedSelectedJob = useCallback(() => {
    if (!selectedJob) return null;
    const selectedJobId = getJobId(selectedJob);
    return {
      ...selectedJob,
      ...localJobUpdates[selectedJobId]
    };
  }, [selectedJob, localJobUpdates, getJobId]);
  // Helper function to merge jobs with local updates
  const getUpdatedJobs = useCallback(() => {
    return jobs.map(job => {
      const jobId = getJobId(job);
      return {
        ...job,
        ...localJobUpdates[jobId]
      };
    });
  }, [jobs, localJobUpdates, getJobId]);

  const hasAssignedEngineeringWork = useCallback((job) => {
    if (!job) return false;
    return Boolean(job.MechanicalEngineer || job.ElectricalEngineer || job.ControlsEngineer);
  }, []);

  const isEngineeringComplete = useCallback((job) => {
    if (!job) return false;

    const mechanicalComplete = !job.MechanicalEngineer || Boolean(job.MechanicalDone);
    const electricalComplete = !job.ElectricalEngineer || Boolean(job.ElectricalDone);
    const controlsComplete = !job.ControlsEngineer || Boolean(job.ControlsDone);

    return hasAssignedEngineeringWork(job) && mechanicalComplete && electricalComplete && controlsComplete;
  }, [hasAssignedEngineeringWork]);

  // Filter and process jobs based on role and current user
  const processJobs = useCallback(() => {
    const updatedJobs = getUpdatedJobs();
    if (!updatedJobs || updatedJobs.length === 0) return [];

    // Filter out shipped jobs first if hideShipped is true
    let filteredData = hideShipped 
      ? updatedJobs.filter(job => job.JobStatus !== "Shipped")
      : updatedJobs;
    
    // Filter jobs based on user role and engineering assignment
    if (roles.Admin) {
      // For admins, show all engineering jobs that have an engineer assigned (even without due date) or have an incomplete task
      return filteredData.filter(job => {
        const hasMechanicalAssignment = job.MechanicalEngineer && !job.MechanicalDone;
        const hasElectricalAssignment = job.ElectricalEngineer && !job.ElectricalDone;
        const hasControlsAssignment = job.ControlsEngineer && !job.ControlsDone;
        
        return hasMechanicalAssignment || hasElectricalAssignment || hasControlsAssignment;
      });
    } else {
      // For engineers, show jobs assigned to them without a "Done" date (regardless of due date)
      return filteredData.filter(job => {
        if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser && !job.MechanicalDone) {
          return true;
        }
        if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser && !job.ElectricalDone) {
          return true;
        }
        if (roles.ControlEngineer && job.ControlsEngineer === currentUser && !job.ControlsDone) {
          return true;
        }
        return false;
      });
    }
  }, [getUpdatedJobs, hideShipped, roles, currentUser]);

  const engineeringJobs = processJobs();

  const processJobsInProduction = useCallback(() => {
    const updatedJobs = getUpdatedJobs();
    if (!updatedJobs || updatedJobs.length === 0) return [];

    const filteredData = hideShipped
      ? updatedJobs.filter(job => job.JobStatus !== "Shipped")
      : updatedJobs;

    return filteredData.filter((job) => {
      if (!isEngineeringComplete(job) || job.ActualShipDate) {
        return false;
      }

      if (roles.Admin) {
        return true;
      }

      if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser) {
        return true;
      }
      if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser) {
        return true;
      }
      if (roles.ControlEngineer && job.ControlsEngineer === currentUser) {
        return true;
      }

      return false;
    });
  }, [getUpdatedJobs, hideShipped, isEngineeringComplete, roles, currentUser]);

  const jobsInProduction = processJobsInProduction();

  // Helper function to get the relevant due date for a job based on user role
  // Engineering Due Date = Review Due Date - 2 days (matching EngineeringCalendar.js logic)
  const getRelevantDueDate = (job) => {
    if (!job) return null;
    const jobId = getJobId(job);
    
    // Get the updated job data that includes local updates
    const updatedJob = {
      ...job,
      ...localJobUpdates[jobId]
    };
    
    // For non-admin users, show their specific engineering due date (review due - 2 days for Mechanical only)
    if (!roles.Admin) {
      if (roles.MechanicalEngineer && updatedJob.MechanicalEngineer === currentUser) {
        const engineeringDue = calculateEngineeringDueDate(
          updatedJob.MechanicalDue,
          2,
          { holidays, ptoEntries, engineerName: updatedJob.MechanicalEngineer }
        );
        return engineeringDue ? engineeringDue.format('YYYY-MM-DD') : updatedJob.MechanicalDue;
      }
      if (roles.ElectricalEngineer && updatedJob.ElectricalEngineer === currentUser) {
        return updatedJob.ElectricalDue;
      }
      if (roles.ControlEngineer && updatedJob.ControlsEngineer === currentUser) {
        return updatedJob.ControlsDue;
      }
    }
    
    // For admin users
    if (roles.Admin) {
      // If admin has selected a specific engineer, show that engineer's engineering due date (2 days before review for Mechanical only)
      if (selectedEngineer) {
        if (updatedJob.MechanicalEngineer === selectedEngineer && !updatedJob.MechanicalDone) {
          const engineeringDue = calculateEngineeringDueDate(
            updatedJob.MechanicalDue,
            2,
            { holidays, ptoEntries, engineerName: updatedJob.MechanicalEngineer }
          );
          return engineeringDue ? engineeringDue.format('YYYY-MM-DD') : updatedJob.MechanicalDue;
        }
        if (updatedJob.ElectricalEngineer === selectedEngineer && !updatedJob.ElectricalDone) {
          return updatedJob.ElectricalDue;
        }
        if (updatedJob.ControlsEngineer === selectedEngineer && !updatedJob.ControlsDone) {
          return updatedJob.ControlsDue;
        }
      } else {
        // If no engineer is selected, show the earliest engineering due date among assigned incomplete tasks (2 days before review for Mechanical only)
        const dueDates = [];
        if (updatedJob.MechanicalEngineer && updatedJob.MechanicalDue && !updatedJob.MechanicalDone) {
          const engineeringDue = calculateEngineeringDueDate(
            updatedJob.MechanicalDue,
            2,
            { holidays, ptoEntries, engineerName: updatedJob.MechanicalEngineer }
          );
          dueDates.push(engineeringDue ? engineeringDue.format('YYYY-MM-DD') : updatedJob.MechanicalDue);
        }
        if (updatedJob.ElectricalEngineer && updatedJob.ElectricalDue && !updatedJob.ElectricalDone) {
          dueDates.push(updatedJob.ElectricalDue);
        }
        if (updatedJob.ControlsEngineer && updatedJob.ControlsDue && !updatedJob.ControlsDone) {
          dueDates.push(updatedJob.ControlsDue);
        }
        
        if (dueDates.length === 0) return null;
        
        // Return the earliest engineering due date (compare as strings since they're in YYYY-MM-DD format)
        return dueDates.sort()[0];
      }
    }
    
    return null;
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleProductionSort = (key) => {
    let direction = 'ascending';
    if (productionSortConfig.key === key && productionSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setProductionSortConfig({ key, direction });
  };

  const sortedJobs = [...engineeringJobs].sort((a, b) => {
    // Special handling for the relevant due date column
    let aValue, bValue;
    
    if (sortConfig.key === 'RelevantDueDate') {
      aValue = getRelevantDueDate(a);
      bValue = getRelevantDueDate(b);
    } else {
      aValue = a[sortConfig.key];
      bValue = b[sortConfig.key];
    }
    
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    
    // For date strings in YYYY-MM-DD format, we can compare them directly
    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const filteredJobs = sortedJobs.filter((job) => {
    // Apply search term filter across JobNumber, ProjectName, and ModelNumber
    const matchesSearch = searchTerm
      ? (job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ModelNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
    
    // Apply engineer filter if admin and engineer is selected
    const matchesEngineer = roles.Admin && selectedEngineer
      ? (job.MechanicalEngineer === selectedEngineer || 
         job.ElectricalEngineer === selectedEngineer || 
         job.ControlsEngineer === selectedEngineer)
      : true;
    
    return matchesSearch && matchesEngineer;
  });

  const sortedProductionJobs = [...jobsInProduction].sort((a, b) => {
    const aValue = a[productionSortConfig.key];
    const bValue = b[productionSortConfig.key];

    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    if (aValue < bValue) {
      return productionSortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return productionSortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const filteredProductionJobs = sortedProductionJobs.filter((job) => {
    const matchesSearch = searchTerm
      ? (job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.ModelNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;

    const matchesEngineer = roles.Admin && selectedEngineer
      ? (job.MechanicalEngineer === selectedEngineer ||
        job.ElectricalEngineer === selectedEngineer ||
        job.ControlsEngineer === selectedEngineer)
      : true;

    return matchesSearch && matchesEngineer;
  });

  const formatDate = (dateString) => {
    return formatDateLocalEnhanced(dateString);
  };

  const handleRowClick = (job) => {
    if (!canOpenPortalJob(job)) return;
    const jobId = getJobId(job);
    if (!jobId) {
      console.warn("Cannot navigate: job ID not found for selected row");
      return;
    }

    // Use the same job selection logic as JobList.js to ensure consistent setup
    dispatch(setJobId(jobId));
    
    // Always set the zone to 1 when a job is selected (same as JobList.js)
    dispatch(setZones(1));
    
    // Attempt to checkout the job (same as JobList.js)
    try {
      const result = dispatch(checkoutJob({ jobId, currentUser }));
      if (checkoutJob.rejected.match(result.payload)) {
        console.warn(result.payload);
      }
    } catch (error) {
      console.error("Failed to check out the job:", error);
    }
    
    navigate(`/${jobId}/generalInfo`);
  };

  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = dueDate.split('-').map(num => parseInt(num, 10));
    const due = new Date(year, month - 1, day);
    due.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= 7 && diffDays >= 0; // Due within a week
  };

  const isPastDue = (dueDate) => {
    if (!dueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = dueDate.split('-').map(num => parseInt(num, 10));
    const due = new Date(year, month - 1, day);
    due.setHours(0, 0, 0, 0); // Reset time to start of day
    
    return due < today;
  };

  // Combine all engineers into a single array for the dropdown
  const getAllEngineers = () => {
    const mechEngineers = userDropdowns?.mechOptions || [];
    const elecEngineers = userDropdowns?.elecOptions || [];
    const controlEngineers = userDropdowns?.controlOptions || [];
    
    // Combine all engineers and remove duplicates
    const allEngineers = [...mechEngineers, ...elecEngineers, ...controlEngineers];
    const uniqueEngineers = [...new Map(allEngineers.map(item => 
      [item.displayName, item])).values()];
      
    return uniqueEngineers.sort((a, b) => 
      a.displayName.localeCompare(b.displayName));
  };

  // Handle opening the completion modal
  const handleMarkAsComplete = (e, job) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedJob(job);
    setSelectedCompletionType('engineering');
    
    // For non-admin users, automatically determine the field to update based on role
    if (!roles.Admin) {
      if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser && !job.MechanicalDone) {
        setCompletionField("MechanicalDone");
      } else if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser && !job.ElectricalDone) {
        setCompletionField("ElectricalDone");
      } else if (roles.ControlEngineer && job.ControlsEngineer === currentUser && !job.ControlsDone) {
        setCompletionField("ControlsDone");
      }
    } else {
      // For admin, default to empty - they will select from the dropdown
      setCompletionField("");
    }
    
    setShowCompletionModal(true);
  };

  // Handle opening the long leads completion modal
  const handleMarkLongLeadsAsComplete = (e, job) => {
    e.stopPropagation(); // Prevent row click event
    
    const projectId = getJobId(job);
    
    // Trigger the longLead fetch for this job's ProjectID
    if (projectId) {
      dispatch(fetchLongLead(projectId));
    }
    
    setSelectedJob(job);
    setSelectedCompletionType('longLeads');
    setLongLeadsCompletionType("Yes"); // Reset to default
    
    // For non-admin users, automatically determine the field to update based on role
    if (!roles.Admin) {
      if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser) {
        setCompletionField("MechanicalLongLeadsDone");
      } else if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser) {
        setCompletionField("ElectricalLongLeadsDone");
      }
    } else {
      // For admin, default to empty - they will select from the dropdown
      setCompletionField("");
    }
    
    setShowCompletionModal(true);
  };

  // Handle closing the completion modal
  const handleCloseCompletionModal = () => {
    setShowCompletionModal(false);
    setSelectedJob(null);
    setCompletionField("");
    setCompletionError(null);
    setCompletionSuccess(false);
    setSelectedCompletionType(null);
    setLongLeadsCompletionType("Yes"); // Reset long leads type
    setEngineeringFeedback({
      CSOUtilizationPercent: null,
      CSOUtilizationReason: "",
      CSOComments: "",
      ReasonLate: "",
      ReasonLateComments: ""
    }); // Reset engineering feedback
    setFeedbackValidationError(null);
  };

  // Handle confirming the completion
  const handleConfirmCompletion = async () => {
    if (!selectedJob || !completionField) return;
    
    // Ensure completionField is a string
    if (typeof completionField !== 'string') {
      console.error('completionField is not a string:', completionField, typeof completionField);
      setCompletionError('Invalid completion field. Please try again.');
      return;
    }
    
    // Validate engineering feedback for mechanical and electrical completions
    if (completionField === 'MechanicalDone' || completionField === 'ElectricalDone') {
      // Check if completion is late and require late reason fields
      if (isCompletionLate(selectedJob, completionField)) {
        if (!engineeringFeedback.ReasonLate) {
          setFeedbackValidationError('Reason Late is required when completing after the due date.');
          return;
        }
        if (engineeringFeedback.ReasonLate === "Other - See Comments" && !engineeringFeedback.ReasonLateComments.trim()) {
          setFeedbackValidationError('Reason Late Comments are required when "Other - See Comments" is selected.');
          return;
        }
      }
      
      // Only validate CSO fields for mechanical completions
      if (completionField === 'MechanicalDone') {
        if (engineeringFeedback.CSOUtilizationReason === "Other - See Comments" && !engineeringFeedback.CSOComments.trim()) {
          setFeedbackValidationError('Comments are required when "Other - See Comments" is selected.');
          return;
        }
      }
      
      setFeedbackValidationError(null);
    }
    
    setSavingCompletion(true);
    setCompletionError(null);
    
    try {
      const selectedJobId = getJobId(selectedJob);
      if (!selectedJobId) {
        throw new Error('Selected job is missing a valid ID');
      }

      // Log the data being sent for debugging
      console.log('Job ID being passed:', selectedJobId);
      console.log('Completion field:', completionField);
      console.log('Completion field type:', typeof completionField);
      
      // Use the callback to update the job
      if (onJobCompletion) {
        // Get today's date in local time, not UTC
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const localDateString = `${year}-${month}-${day}`;
        
        // For long leads, we need to update two fields in one call
        if (selectedCompletionType === 'longLeads') {
          // Get the value field name by removing 'Done' from the completion field
          const valueField = completionField.replace('Done', ''); // Remove 'Done' to get the value field name
          
          // Send the job ID and the update data as separate parameters
          await onJobCompletion(selectedJobId, {
            [valueField]: longLeadsCompletionType,  // Set the long leads value (e.g., "Yes" or "Yes - Secondary Long Leads")
            [completionField]: localDateString  // Set the done date with local date
          });
          
          // Update local state to reflect both changes
          setLocalJobUpdates(prev => ({
            ...prev,
            [selectedJobId]: {
              ...prev[selectedJobId],
              [valueField]: longLeadsCompletionType,
              [completionField]: localDateString
            }
          }));
        } else {
          // Regular engineering completion
          let updateData = {};
          
          // Special handling for MechanicalDone
          if (completionField === 'MechanicalDone') {
            updateData = {
              MechanicalReviewDate: localDateString,  // Use local date string
              MechanicalDone: 'R'                      // Set to "R" instead of date
            };
            
            // Determine user role for this completion
            let userRole = '';
            if (roles.MechanicalEngineer) {
              userRole = 'MechanicalEngineer';
            } else if (roles.Admin) {
              userRole = 'Admin';
            }
            
            // Post engineering feedback to the engineering-feedback API
            if (engineeringFeedback.CSOUtilizationReason || engineeringFeedback.CSOComments || engineeringFeedback.ReasonLate) {
              try {
                const feedbackData = buildEngineeringFeedbackPayload(selectedJob, userRole, {
                  CSOUtilizationPercent: engineeringFeedback.CSOUtilizationPercent,
                  CSOUtilizationReason: engineeringFeedback.CSOUtilizationReason,
                  CSOComments: engineeringFeedback.CSOComments,
                  ReasonLate: engineeringFeedback.ReasonLate || null,
                  ReasonLateComments: engineeringFeedback.ReasonLateComments || null
                });
                
                console.log('Posting engineering feedback:', feedbackData);
                
                // Get API URL from Redux store
                const engineeringFeedbackUrl = apiUrls?.engineeringFeedback;
                if (engineeringFeedbackUrl) {
                  const response = await axiosInstance.post(engineeringFeedbackUrl, feedbackData);
                  
                  if (response.status === 200 || response.status === 201) {
                    console.log('Engineering feedback posted successfully');
                  }
                } else {
                  console.error('Engineering feedback API URL not found in apiUrls');
                }
              } catch (feedbackError) {
                console.error('Error posting engineering feedback:', feedbackError);
                // Don't fail the entire completion if feedback posting fails
              }
            }
          } else if (completionField === 'ElectricalDone') {
            // Special handling for ElectricalDone - now includes feedback
            updateData = {
              [completionField]: localDateString
            };
            
            // Determine user role for this completion
            let userRole = '';
            if (roles.ElectricalEngineer) {
              userRole = 'ElectricalEngineer';
            } else if (roles.Admin) {
              userRole = 'Admin';
            }
            
            // Post engineering feedback if late completion
            if (engineeringFeedback.ReasonLate) {
              try {
                const feedbackData = buildEngineeringFeedbackPayload(selectedJob, userRole, {
                  CSOUtilizationPercent: 0.0,
                  CSOUtilizationReason: null,
                  CSOComments: null,
                  ReasonLate: engineeringFeedback.ReasonLate,
                  ReasonLateComments: engineeringFeedback.ReasonLateComments || null
                });
                
                console.log('Posting electrical engineering feedback:', feedbackData);
                
                // Get API URL from Redux store
                const engineeringFeedbackUrl = apiUrls?.engineeringFeedback;
                if (engineeringFeedbackUrl) {
                  const response = await axiosInstance.post(engineeringFeedbackUrl, feedbackData);
                  
                  if (response.status === 200 || response.status === 201) {
                    console.log('Electrical engineering feedback posted successfully');
                  }
                } else {
                  console.error('Engineering feedback API URL not found in apiUrls');
                }
              } catch (feedbackError) {
                console.error('Error posting electrical engineering feedback:', feedbackError);
                // Don't fail the entire completion if feedback posting fails
              }
            }
          } else {
            // For Controls, keep the original behavior (set date)
            updateData = {
              [completionField]: localDateString
            };
          }
          
          await onJobCompletion(selectedJobId, updateData);
          
          // Update local state to reflect the change immediately
          setLocalJobUpdates(prev => ({
            ...prev,
            [selectedJobId]: {
              ...prev[selectedJobId],
              ...updateData
            }
          }));
        }
        
        // Reset the completion field since this task is now complete
        setCompletionField("");
      }
      
      // Set success message
      setCompletionSuccess(true);
      
      // Close the modal after a brief delay to show success message
      setTimeout(() => {
        handleCloseCompletionModal();
      }, 1500);
      
    } catch (error) {
      console.error("Error marking job as complete:", error);
      setCompletionError("Failed to mark job as complete. Please try again.");
    } finally {
      setSavingCompletion(false);
    }
  };
  // Determine whether a role is eligible to mark completion based on job and user
  const canMarkAsComplete = (job) => {
    if (!job) return false;
    
    if (roles.Admin) {
      // Admin can mark complete if any engineering field has an engineer assigned but no done date
      const hasPendingMechanical = job.MechanicalEngineer && !job.MechanicalDone;
      const hasPendingElectrical = job.ElectricalEngineer && !job.ElectricalDone;
      const hasPendingControls = job.ControlsEngineer && !job.ControlsDone;
      
      return hasPendingMechanical || hasPendingElectrical || hasPendingControls;
    }
    
    // For regular engineers, check their specific role (due date not required)
    if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser && !job.MechanicalDone) {
      return true;
    }
    if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser && !job.ElectricalDone) {
      return true;
    }
    if (roles.ControlEngineer && job.ControlsEngineer === currentUser && !job.ControlsDone) {
      return true;
    }
    
    return false;
  };

  // Determine if long leads can be marked as complete
  const canMarkLongLeadsAsComplete = (job) => {
    if (!job) return false;
    const jobId = getJobId(job);
    
    // Get the updated job data that includes local updates
    const updatedJob = {
      ...job,
      ...localJobUpdates[jobId]
    };
    
    if (roles.Admin) {
      // Admin can mark complete if any engineer is assigned and long leads is not done
      // Check both PascalCase and camelCase variations
      const mechanicalLongLeadsDone = updatedJob.MechanicalLongLeadsDone ?? updatedJob.mechanicalLongLeadsDone;
      const electricalLongLeadsDone = updatedJob.ElectricalLongLeadsDone ?? updatedJob.electricalLongLeadsDone;
      
      const hasPendingMechanical = updatedJob.MechanicalEngineer && !mechanicalLongLeadsDone;
      const hasPendingElectrical = updatedJob.ElectricalEngineer && !electricalLongLeadsDone;
      
      return hasPendingMechanical || hasPendingElectrical;
    }
    
    // For regular engineers, check their specific role
    // Check both PascalCase and camelCase variations
    const mechanicalLongLeadsDone = updatedJob.MechanicalLongLeadsDone ?? updatedJob.mechanicalLongLeadsDone;
    const electricalLongLeadsDone = updatedJob.ElectricalLongLeadsDone ?? updatedJob.electricalLongLeadsDone;
    
    if (roles.MechanicalEngineer && updatedJob.MechanicalEngineer === currentUser && !mechanicalLongLeadsDone) {
      return true;
    }
    if (roles.ElectricalEngineer && updatedJob.ElectricalEngineer === currentUser && !electricalLongLeadsDone) {
      return true;
    }
    
    return false;
  };

  // Helper function to get the appropriate notes field based on user role and job
  const getNotesField = (job) => {
    if (!job) return null;
    
    if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser) {
      return "MechanicalEngineerNotes";
    }
    if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser) {
      return "ElectricalEngineerNotes";
    }
    if (roles.ControlEngineer && job.ControlsEngineer === currentUser) {
      return "ControlsEngineerNotes";
    }
    
    return null;
  };

  // Helper function to get current notes value for a job
  const getCurrentNotes = (job, field) => {
    if (!job || !field) return "";
    const jobId = getJobId(job);
    
    // Check local updates first
    if (localJobUpdates[jobId] && localJobUpdates[jobId][field] !== undefined) {
      return localJobUpdates[jobId][field] || "";
    }
    
    return job[field] || "";
  };

  // Handle opening the notes modal
  const handleOpenNotesModal = (e, job) => {
    e.stopPropagation();
    setSelectedJobForNotes(job);
    
    let field = "";
    if (roles.Admin) {
      // For admin, they'll select which notes field to edit
      field = "";
    } else {
      // For engineers, automatically determine the field
      field = getNotesField(job);
    }
    
    setNotesField(field);
    const currentNotesValue = field ? getCurrentNotes(job, field) : "";
    setNotesValue(currentNotesValue);
    setOriginalNotesValue(currentNotesValue);
    setNotesError(null);
    setNotesSuccess(false);
    setShowNotesModal(true);
  };

  // Handle closing the notes modal
  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedJobForNotes(null);
    setNotesField("");
    setNotesValue("");
    setOriginalNotesValue("");
    setNotesError(null);
    setNotesSuccess(false);
  };

  // Handle saving notes
  const handleSaveNotes = async () => {
    if (!selectedJobForNotes || !notesField) return;
    
    // Don't save if notes haven't changed
    if (notesValue === originalNotesValue) {
      handleCloseNotesModal();
      return;
    }
    
    setSavingNotes(true);
    setNotesError(null);
    
    try {
      if (onJobCompletion) {
        await onJobCompletion(selectedJobForNotes.ID, {
          [notesField]: notesValue
        });
        
        // Update local state immediately
        setLocalJobUpdates(prev => ({
          ...prev,
          [selectedJobForNotes.ID]: {
            ...prev[selectedJobForNotes.ID],
            [notesField]: notesValue
          }
        }));
        
        setNotesSuccess(true);
        
        // Close modal after brief delay
        setTimeout(() => {
          handleCloseNotesModal();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      setNotesError("Failed to save notes. Please try again.");
    } finally {
      setSavingNotes(false);
    }
  };

  // Check if user can edit notes for a job
  const canEditNotes = (job) => {
    if (!job) return false;
    
    if (roles.Admin) {
      // Admin can edit notes if any engineer is assigned
      return job.MechanicalEngineer || job.ElectricalEngineer || job.ControlsEngineer;
    }
    
    // Engineers can edit notes if they're assigned to the job
    return getNotesField(job) !== null;
  };

  return (
    <div className="m-4">
      {error && <Alert variant="danger">{error}</Alert>}
      <EngineeringPortalNoticeBanner />
      <div className="mb-3 text-center"><h1>Assigned Engineering Jobs</h1></div>
      <Row className="mb-3">
        <Col md={roles.Admin ? 8 : 10}>
          <Form.Control
            type="text"
            placeholder="Search by Job Number, Project Name, or Model Number"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        {roles.Admin && (
          <Col md={2}>
            <Form.Select
              value={selectedEngineer}
              onChange={(e) => setSelectedEngineer(e.target.value)}
              aria-label="Filter by Engineer"
            >
              <option value="">Filter by Engineer</option>
              {getAllEngineers().map((engineer) => (
                <option key={engineer.displayName} value={engineer.displayName}>
                  {engineer.displayName}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
        <Col md={2}>
          <Form.Check
            type="switch"
            id="hide-shipped-switch"
            label="Hide Shipped Jobs"
            checked={hideShipped}            onChange={() => setHideShipped(!hideShipped)}
            className="mt-2"
          />
        </Col>
      </Row>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th onClick={() => handleSort('JobNumber')}>Job Number</th>
            <th onClick={() => handleSort('ProjectName')}>Project Name</th>
            <th onClick={() => handleSort('ModelNumber')}>Model Number</th>
            <th onClick={() => handleSort('ProductionRelease')}>Release To Scheduling</th>
            <th onClick={() => handleSort('RelevantDueDate')}>Engineering Due</th>
            <th onClick={() => handleSort('ProjectManager')}>Project Manager</th>
            {roles.Admin && <th>Assigned Engineers</th>}
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.map((job) => {
            const jobId = getJobId(job);
            const relevantDueDate = getRelevantDueDate(job);
            const notesField = getNotesField(job);
            const currentNotes = getCurrentNotes(job, notesField);
            
            return (
              <tr 
                key={jobId || `${job.JobNumber}-${job.ProjectName}`} 
                onClick={canOpenPortalJob(job) ? () => handleRowClick(job) : undefined}
                style={{ 
                  cursor: canOpenPortalJob(job) ? 'pointer' : 'default',
                  backgroundColor: isPastDue(relevantDueDate) ? '#ffdddd' : 
                                   isDueSoon(relevantDueDate) ? '#ffffcc' : ''
                }}
              >
                <td>
                  {job.JobNumber}
                  {hasTbd(jobId) && (
                    <i
                      className="bi bi-exclamation-lg ms-1"
                      style={{ color: "red" }}
                      title="This job has TBDs"
                    ></i>
                  )}
                  {hasIncompleteTasks(jobId) && (
                    <i
                      className="bi bi-person-lines-fill ms-1"
                      style={{ color: "orange" }}
                      title="This job has incomplete change order tasks"
                    ></i>
                  )}
                </td>
                <td>{job.ProjectName}</td>
                <td>{job.ModelNumber}</td>
                <td>{formatDate(job.ProductionRelease)}</td>
                <td>
                  {(() => {
                    const dueDateDisplay = formatDate(relevantDueDate);
                    
                    // For non-admins, show which type of due date this is
                    let dueDateLabel = '';
                    if (!roles.Admin && relevantDueDate) {
                      if (roles.MechanicalEngineer && job.MechanicalEngineer === currentUser) {
                        dueDateLabel = ' (Mechanical)';
                      } else if (roles.ElectricalEngineer && job.ElectricalEngineer === currentUser) {
                        dueDateLabel = ' (Electrical)';
                      } else if (roles.ControlEngineer && job.ControlsEngineer === currentUser) {
                        dueDateLabel = ' (Controls)';
                      }
                    }
                    
                    // For admins with selected engineer, show which type of due date this is
                    if (roles.Admin && selectedEngineer && relevantDueDate) {
                      if (job.MechanicalEngineer === selectedEngineer) {
                        dueDateLabel = ' (M)';
                      } else if (job.ElectricalEngineer === selectedEngineer) {
                        dueDateLabel = ' (E)';
                      } else if (job.ControlsEngineer === selectedEngineer) {
                        dueDateLabel = ' (C)';
                      }
                    }
                    
                    return (
                      <>
                        {dueDateDisplay ? (
                          <>
                            {dueDateDisplay}{dueDateLabel}
                            {isPastDue(relevantDueDate) && 
                              <span className="ms-2 badge bg-danger">Past Due</span>
                            }
                            {isDueSoon(relevantDueDate) && !isPastDue(relevantDueDate) && 
                              <span className="ms-2 badge bg-warning text-dark">Due Soon</span>
                            }
                          </>
                        ) : (
                          <span className="text-muted">No due date set</span>
                        )}
                      </>
                    );
                  })()}
                </td>
                <td>{job.ProjectManager}</td>
                {roles.Admin && (
                  <td>
                    {job.MechanicalEngineer && <div><small className="text-muted">Mech:</small> {job.MechanicalEngineer}</div>}
                    {job.ElectricalEngineer && <div><small className="text-muted">Elec:</small> {job.ElectricalEngineer}</div>}
                    {job.ControlsEngineer && <div><small className="text-muted">Controls:</small> {job.ControlsEngineer}</div>}
                  </td>
                )}
                <td>
                  {/* Show notes preview for the relevant engineer */}
                  {!roles.Admin && currentNotes && (
                    <div className="text-truncate" style={{ maxWidth: '200px' }} title={currentNotes}>
                      <small>{currentNotes}</small>
                    </div>
                  )}
                  {!roles.Admin && !currentNotes && (
                    <small className="text-muted">No notes</small>
                  )}
                  
                  {/* For admin, show all engineering notes */}
                  {roles.Admin && (
                    <div>
                      {(getCurrentNotes(job, "MechanicalEngineerNotes") || 
                        getCurrentNotes(job, "ElectricalEngineerNotes") || 
                        getCurrentNotes(job, "ControlsEngineerNotes")) ? (
                        <>
                          {getCurrentNotes(job, "MechanicalEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "MechanicalEngineerNotes")}>
                              <small><span className="text-muted">M:</span> {getCurrentNotes(job, "MechanicalEngineerNotes")}</small>
                            </div>
                          )}
                          {getCurrentNotes(job, "ElectricalEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "ElectricalEngineerNotes")}>
                              <small><span className="text-muted">E:</span> {getCurrentNotes(job, "ElectricalEngineerNotes")}</small>
                            </div>
                          )}
                          {getCurrentNotes(job, "ControlsEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "ControlsEngineerNotes")}>
                              <small><span className="text-muted">C:</span> {getCurrentNotes(job, "ControlsEngineerNotes")}</small>
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="text-muted">No notes</small>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <div className="d-flex flex-column gap-2">
                    {/* Notes button */}
                    {canEditNotes(job) && (
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={(e) => handleOpenNotesModal(e, job)}
                        className="w-100"
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        {!roles.Admin && currentNotes ? 'Edit Notes' : 'Add/Edit Notes'}
                      </Button>
                    )}
                    
                    {/* Engineering completion button */}
                    {canMarkAsComplete(job) && (
                      <Button 
                        variant="success" 
                        size="sm" 
                        onClick={(e) => handleMarkAsComplete(e, job)}
                        className="w-100"
                      >
                        <i className="bi bi-check-circle me-1"></i>
                        Mark Engineering Complete
                      </Button>
                    )}
                    
                    {/* Long Leads completion button */}
                    {canMarkLongLeadsAsComplete(job) && (
                      <Button 
                        variant="info" 
                        size="sm" 
                        onClick={(e) => handleMarkLongLeadsAsComplete(e, job)}
                        className="w-100"
                      >
                        <i className="bi bi-box-seam me-1"></i>
                        Mark Long Leads Complete
                      </Button>
                    )}
                    
                    {/* Show when nothing can be done */}
                    {!canEditNotes(job) && !canMarkAsComplete(job) && !canMarkLongLeadsAsComplete(job) && (
                      <span className="text-muted">No actions available</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {!loading && filteredJobs.length === 0 &&
        <Alert variant="info">No engineering jobs assigned to you.</Alert>
      }

      <div className="mt-5 mb-3 text-center"><h2>Jobs In Production</h2></div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th onClick={() => handleProductionSort('JobNumber')}>Job Number</th>
            <th onClick={() => handleProductionSort('ProjectName')}>Project Name</th>
            <th onClick={() => handleProductionSort('ModelNumber')}>Model Number</th>
            <th onClick={() => handleProductionSort('ProductionRelease')}>Release To Scheduling</th>
            <th onClick={() => handleProductionSort('CurrentPlanShipDate')}>Current Plan Ship Date</th>
            <th onClick={() => handleProductionSort('ProjectManager')}>Project Manager</th>
            {roles.Admin && <th>Assigned Engineers</th>}
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredProductionJobs.map((job) => {
            const jobId = getJobId(job);
            const notesField = getNotesField(job);
            const currentNotes = getCurrentNotes(job, notesField);

            return (
              <tr
                key={`production-${jobId || `${job.JobNumber}-${job.ProjectName}`}`}
                onClick={canOpenPortalJob(job) ? () => handleRowClick(job) : undefined}
                style={{
                  cursor: canOpenPortalJob(job) ? 'pointer' : 'default',
                  backgroundColor: isPastDue(job.CurrentPlanShipDate) ? '#ffdddd' :
                    isDueSoon(job.CurrentPlanShipDate) ? '#ffffcc' : ''
                }}
              >
                <td>
                  {job.JobNumber}
                  {hasTbd(jobId) && (
                    <i
                      className="bi bi-exclamation-lg ms-1"
                      style={{ color: "red" }}
                      title="This job has TBDs"
                    ></i>
                  )}
                  {hasIncompleteTasks(jobId) && (
                    <i
                      className="bi bi-person-lines-fill ms-1"
                      style={{ color: "orange" }}
                      title="This job has incomplete change order tasks"
                    ></i>
                  )}
                </td>
                <td>{job.ProjectName}</td>
                <td>{job.ModelNumber}</td>
                <td>{formatDate(job.ProductionRelease)}</td>
                <td>
                  {formatDate(job.CurrentPlanShipDate) ? (
                    <>
                      {formatDate(job.CurrentPlanShipDate)}
                      {isPastDue(job.CurrentPlanShipDate) &&
                        <span className="ms-2 badge bg-danger">Past Due</span>
                      }
                      {isDueSoon(job.CurrentPlanShipDate) && !isPastDue(job.CurrentPlanShipDate) &&
                        <span className="ms-2 badge bg-warning text-dark">Due Soon</span>
                      }
                    </>
                  ) : (
                    <span className="text-muted">No ship date set</span>
                  )}
                </td>
                <td>{job.ProjectManager}</td>
                {roles.Admin && (
                  <td>
                    {job.MechanicalEngineer && <div><small className="text-muted">Mech:</small> {job.MechanicalEngineer}</div>}
                    {job.ElectricalEngineer && <div><small className="text-muted">Elec:</small> {job.ElectricalEngineer}</div>}
                    {job.ControlsEngineer && <div><small className="text-muted">Controls:</small> {job.ControlsEngineer}</div>}
                  </td>
                )}
                <td>
                  {!roles.Admin && currentNotes && (
                    <div className="text-truncate" style={{ maxWidth: '200px' }} title={currentNotes}>
                      <small>{currentNotes}</small>
                    </div>
                  )}
                  {!roles.Admin && !currentNotes && (
                    <small className="text-muted">No notes</small>
                  )}

                  {roles.Admin && (
                    <div>
                      {(getCurrentNotes(job, "MechanicalEngineerNotes") ||
                        getCurrentNotes(job, "ElectricalEngineerNotes") ||
                        getCurrentNotes(job, "ControlsEngineerNotes")) ? (
                        <>
                          {getCurrentNotes(job, "MechanicalEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "MechanicalEngineerNotes")}>
                              <small><span className="text-muted">M:</span> {getCurrentNotes(job, "MechanicalEngineerNotes")}</small>
                            </div>
                          )}
                          {getCurrentNotes(job, "ElectricalEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "ElectricalEngineerNotes")}>
                              <small><span className="text-muted">E:</span> {getCurrentNotes(job, "ElectricalEngineerNotes")}</small>
                            </div>
                          )}
                          {getCurrentNotes(job, "ControlsEngineerNotes") && (
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={getCurrentNotes(job, "ControlsEngineerNotes")}>
                              <small><span className="text-muted">C:</span> {getCurrentNotes(job, "ControlsEngineerNotes")}</small>
                            </div>
                          )}
                        </>
                      ) : (
                        <small className="text-muted">No notes</small>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <div className="d-flex flex-column gap-2">
                    {canEditNotes(job) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => handleOpenNotesModal(e, job)}
                        className="w-100"
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        {!roles.Admin && currentNotes ? 'Edit Notes' : 'Add/Edit Notes'}
                      </Button>
                    )}

                    {!canEditNotes(job) && (
                      <span className="text-muted">No actions available</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {!loading && filteredProductionJobs.length === 0 &&
        <Alert variant="info">No jobs in production.</Alert>
      }

      {loading && <div className="text-center"><Spinner animation="border" /></div>}

      {/* Notes Modal */}
      <Modal show={showNotesModal} onHide={handleCloseNotesModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Engineering Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {notesError && <Alert variant="danger">{notesError}</Alert>}
          {notesSuccess && <Alert variant="success">Notes saved successfully!</Alert>}
          
          {!notesSuccess && (
            <>
              <div className="mb-3">
                <h6>Job Details</h6>
                <p className="mb-1"><strong>Job Number:</strong> {selectedJobForNotes?.JobNumber}</p>
                <p className="mb-1"><strong>Project Name:</strong> {selectedJobForNotes?.ProjectName}</p>
                <p><strong>Model Number:</strong> {selectedJobForNotes?.ModelNumber}</p>
              </div>
              
              {roles.Admin && (
                <Form.Group className="mb-3">
                  <Form.Label>Select Notes Field</Form.Label>
                  <Form.Select
                    value={notesField}
                    onChange={(e) => {
                      setNotesField(e.target.value);
                      setNotesValue(getCurrentNotes(selectedJobForNotes, e.target.value));
                      setOriginalNotesValue(getCurrentNotes(selectedJobForNotes, e.target.value));
                    }}
                  >
                    <option value="">Select Notes Field</option>
                    {selectedJobForNotes?.MechanicalEngineer && (
                      <option value="MechanicalEngineerNotes">
                        Mechanical Engineer Notes ({selectedJobForNotes.MechanicalEngineer})
                      </option>
                    )}
                    {selectedJobForNotes?.ElectricalEngineer && (
                      <option value="ElectricalEngineerNotes">
                        Electrical Engineer Notes ({selectedJobForNotes.ElectricalEngineer})
                      </option>
                    )}
                    {selectedJobForNotes?.ControlsEngineer && (
                      <option value="ControlsEngineerNotes">
                        Controls Engineer Notes ({selectedJobForNotes.ControlsEngineer})
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              )}
              
              {(!roles.Admin || notesField) && (
                <Form.Group>
                  <Form.Label>
                    {!roles.Admin ? 'Your Notes' : 'Notes'}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    placeholder="Enter your notes here..."
                  />
                  <Form.Text className="text-muted">
                    These notes will be saved to the job and visible to other team members.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseNotesModal} disabled={savingNotes}>
            Close
          </Button>
          {!notesSuccess && (
            <Button 
              variant="primary" 
              onClick={handleSaveNotes} 
              disabled={savingNotes || !notesField || notesValue === originalNotesValue}
            >
              {savingNotes ? "Saving..." : "Save Notes"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Completion Modal with improved UI for different completion types */}
      <Modal show={showCompletionModal} onHide={handleCloseCompletionModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {completionField?.includes('LongLeads') 
              ? 'Mark Long Leads as Complete' 
              : 'Mark Engineering as Complete'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {completionError && <Alert variant="danger">{completionError}</Alert>}
          {completionSuccess && (
            <Alert variant="success">
              {completionField?.includes('LongLeads') 
                ? 'Long Leads marked as complete successfully!' 
                : 'Engineering task marked as complete successfully!'}
            </Alert>
          )}
          {!completionSuccess && (
            <>              <div className="mb-3">
                <h6>Job Details</h6>
                <p className="mb-1"><strong>Job Number:</strong> {getUpdatedSelectedJob()?.JobNumber}</p>
                <p className="mb-1"><strong>Project Name:</strong> {getUpdatedSelectedJob()?.ProjectName}</p>
                <p><strong>Model Number:</strong> {getUpdatedSelectedJob()?.ModelNumber}</p>
              </div>
              
              {roles.Admin && (
                <Form.Group controlId="completionFieldSelect">
                  <Form.Label>Select Completion Field</Form.Label>
                  
                  {/* Conditional rendering based on which button was clicked */}
                  {selectedCompletionType === 'engineering' ? (
                    <>
                      <Form.Select
                        value={completionField}
                        onChange={(e) => setCompletionField(e.target.value)}
                      >
                        <option value="">Select Engineering Field</option>
                        {getUpdatedSelectedJob()?.MechanicalEngineer && !getUpdatedSelectedJob()?.MechanicalDone && (
                          <option value="MechanicalDone">Mechanical Done</option>
                        )}
                        {getUpdatedSelectedJob()?.ElectricalEngineer && !getUpdatedSelectedJob()?.ElectricalDone && (
                          <option value="ElectricalDone">Electrical Done</option>
                        )}
                        {getUpdatedSelectedJob()?.ControlsEngineer && !getUpdatedSelectedJob()?.ControlsDone && (
                          <option value="ControlsDone">Controls Done</option>
                        )}
                      </Form.Select>
                    </>
                  ) : (
                    <>
                      {/* Long Leads Criteria Alert */}
                      {selectedCompletionType === 'longLeads' && getLongLeadCriteriaFlag(selectedJob) && (
                        <Alert variant="warning" className="mb-3">
                          <p className="mb-2">
                            This job may include items typically included with Secondary BOMs but are uncommon in their type/usage, such that Purchasing needs advance notice to place on order asap (e.g., large motor cable, liquid tight conduit and fittings, anything metric or with special material/coatings, etc.).
                          </p>
                          <p className="mb-0">
                            <strong>Action Required:</strong> A review meeting with Purchasing and Assembly team leads should be co-hosted by assigned ME and EE prior to final design release to Production. All uncommon items identified should then be released in the ME and/or EE Engineering Primary BOMs.
                          </p>
                        </Alert>
                      )}
                      
                      <Form.Select
                        value={completionField}
                        onChange={(e) => setCompletionField(e.target.value)}
                        className="mb-3"
                      >
                        <option value="">Select Long Leads Field</option>
                        {getUpdatedSelectedJob()?.MechanicalEngineer && 
                         !(getUpdatedSelectedJob()?.MechanicalLongLeadsDone ?? getUpdatedSelectedJob()?.mechanicalLongLeadsDone) && (
                          <option value="MechanicalLongLeadsDone">Mechanical Long Leads</option>
                        )}
                        {getUpdatedSelectedJob()?.ElectricalEngineer && 
                         !(getUpdatedSelectedJob()?.ElectricalLongLeadsDone ?? getUpdatedSelectedJob()?.electricalLongLeadsDone) && (
                          <option value="ElectricalLongLeadsDone">Electrical Long Leads</option>
                        )}
                      </Form.Select>
                      
                      {completionField && (
                        <Form.Group controlId="longLeadsTypeSelect">
                          <Form.Label>Long Leads Completion Type</Form.Label>
                          <Form.Select
                            value={longLeadsCompletionType}
                            onChange={(e) => setLongLeadsCompletionType(e.target.value)}
                          >
                            <option value="Yes">Yes</option>
                            <option value="Yes - Secondary Long Leads">Yes - Secondary Long Leads</option>
                            <option value="N/A">N/A</option>
                          </Form.Select>
                        </Form.Group>
                      )}
                    </>
                  )}
                </Form.Group>
              )}
              
              {/* Engineering Feedback Form - Show for both Admin and Engineers */}
              {selectedCompletionType === 'engineering' && completionField === 'MechanicalDone' && (
                <div className="mt-4 pt-4 border-top">
                  <h6>Mechanical Engineering Feedback</h6>
                  
                  {feedbackValidationError && (
                    <Alert variant="warning" className="mb-3">
                      {feedbackValidationError}
                    </Alert>
                  )}
                  
                  {/* Late Completion Fields - Show if completing after due date */}
                  {isCompletionLate(selectedJob, completionField) && (
                    <div className="mb-4 pb-4 border-bottom">
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Reason Late <span className="text-danger">*Required</span>
                        </Form.Label>
                        <Form.Select
                          value={engineeringFeedback.ReasonLate}
                          onChange={(e) => setEngineeringFeedback({
                            ...engineeringFeedback,
                            ReasonLate: e.target.value
                          })}
                        >
                          <option value="">Select Reason</option>
                          {reasonLateOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Reason Late Comments
                          {engineeringFeedback.ReasonLate === "Other - See Comments" && (
                            <span className="text-danger"> *Required</span>
                          )}
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={engineeringFeedback.ReasonLateComments}
                          onChange={(e) => setEngineeringFeedback({
                            ...engineeringFeedback,
                            ReasonLateComments: e.target.value
                          })}
                          placeholder="Provide additional details about why this completion is late"
                        />
                      </Form.Group>
                    </div>
                  )}
                  
                  {/* CSO Utilization Percent Slider */}
                  <Form.Group className="mb-4">
                    <Form.Label>
                      <span className="text-danger">*</span> CSO Utilization Percent: <strong>{engineeringFeedback.CSOUtilizationPercent === null ? '--' : `${Math.round(engineeringFeedback.CSOUtilizationPercent * 100)}%`}</strong>
                    </Form.Label>
                    <Form.Range
                      min="0"
                      max="1"
                      step="0.1"
                      value={engineeringFeedback.CSOUtilizationPercent ?? 0}
                      onChange={(e) => setEngineeringFeedback({
                        ...engineeringFeedback,
                        CSOUtilizationPercent: parseFloat(e.target.value)
                      })}
                    />
                    <Form.Text className="text-muted">
                      Use slider to set utilization percentage (0% - 100%)
                    </Form.Text>
                  </Form.Group>
                  
                  {/* CSO Utilization Reason Dropdown */}
                  <Form.Group className="mb-4">
                    <Form.Label><span className="text-danger">*</span> CSO Utilization Reason</Form.Label>
                    <Form.Select
                      value={engineeringFeedback.CSOUtilizationReason}
                      onChange={(e) => setEngineeringFeedback({
                        ...engineeringFeedback,
                        CSOUtilizationReason: e.target.value
                      })}
                    >
                      <option value="">Select Reason</option>
                      {csoUtilizationReasonOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  {/* Comments Textarea */}
                  <Form.Group>
                    <Form.Label>
                      Comments 
                      {engineeringFeedback.CSOUtilizationReason === "Other - See Comments" && (
                        <span className="text-danger"> *Required</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={engineeringFeedback.CSOComments}
                      onChange={(e) => setEngineeringFeedback({
                        ...engineeringFeedback,
                        CSOComments: e.target.value
                      })}
                      placeholder="Enter engineering feedback comments (required if 'Other - See Comments' is selected)"
                    />
                  </Form.Group>
                </div>
              )}
              
              {/* Engineering Feedback Form for Electrical Done */}
              {selectedCompletionType === 'engineering' && completionField === 'ElectricalDone' && (
                <div className="mt-4 pt-4 border-top">
                  <h6>Electrical Engineering Feedback</h6>
                  
                  {feedbackValidationError && (
                    <Alert variant="warning" className="mb-3">
                      {feedbackValidationError}
                    </Alert>
                  )}
                  
                  {/* Late Completion Fields - Show if completing after due date */}
                  {isCompletionLate(selectedJob, completionField) && (
                    <div className="mb-4">
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Reason Late <span className="text-danger">*Required</span>
                        </Form.Label>
                        <Form.Select
                          value={engineeringFeedback.ReasonLate}
                          onChange={(e) => setEngineeringFeedback({
                            ...engineeringFeedback,
                            ReasonLate: e.target.value
                          })}
                        >
                          <option value="">Select Reason</option>
                          {reasonLateOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Reason Late Comments
                          {engineeringFeedback.ReasonLate === "Other - See Comments" && (
                            <span className="text-danger"> *Required</span>
                          )}
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={engineeringFeedback.ReasonLateComments}
                          onChange={(e) => setEngineeringFeedback({
                            ...engineeringFeedback,
                            ReasonLateComments: e.target.value
                          })}
                          placeholder="Provide additional details about why this completion is late"
                        />
                      </Form.Group>
                    </div>
                  )}
                </div>
              )}
              
              {/* For non-admin users, show the long leads type dropdown when marking long leads complete */}
              {!roles.Admin && selectedCompletionType === 'longLeads' && (
                <>
                  {/* Long Leads Criteria Alert for non-admin users */}
                  {getLongLeadCriteriaFlag(selectedJob) && (
                    <Alert variant="warning" className="mb-3">
                      <Alert.Heading>⚠️ Long Lead Criteria Met</Alert.Heading>
                      <p className="mb-2">
                        This job may include items typically included with Secondary BOMs but are uncommon in their type/usage, such that Purchasing needs advance notice to place on order asap (e.g., large motor cable, liquid tight conduit and fittings, anything metric or with special material/coatings, etc.).
                      </p>
                      <p className="mb-0">
                        <strong>Action Required:</strong> A review meeting with Purchasing and Assembly team leads should be co-hosted by assigned ME and EE prior to final design release to Production. All uncommon items identified should then be released in the ME and/or EE Engineering Primary BOMs.
                      </p>
                    </Alert>
                  )}
                  
                  <Form.Group controlId="longLeadsTypeSelect">
                    <Form.Label>Long Leads Completion Type</Form.Label>
                    <Form.Select
                      value={longLeadsCompletionType}
                      onChange={(e) => setLongLeadsCompletionType(e.target.value)}
                    >
                      <option value="Yes">Yes</option>
                      <option value="Yes - Secondary Long Leads">Yes - Secondary Long Leads</option>
                      <option value="N/A">N/A</option>
                    </Form.Select>
                  </Form.Group>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCompletionModal} disabled={savingCompletion}>
            Close
          </Button>
          {!completionSuccess && (
            <Button 
              variant={completionField?.includes('LongLeads') ? "info" : "success"}
              onClick={handleConfirmCompletion} 
              disabled={savingCompletion || !completionField}
            >
              {savingCompletion ? "Saving..." : (
                completionField?.includes('LongLeads') 
                  ? "Confirm Long Leads Completion" 
                  : "Confirm Engineering Completion"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AssignedEngineeringJobs;
